import MenuItem from '@mui/material/MenuItem';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import type { Option } from '@openx/types/options';
import { useCallback } from 'react';
import type { DropdownConfigurableFilter } from '../../types';

type DropdownSelectorProps = {
  selectedOptions: string | undefined;
  onOptionChange: (options: string | string[]) => void;
  isDisabled?: boolean;
  options?: Option[];
  config?: DropdownConfigurableFilter<string>['config'];
};

export const DropdownSelector = ({
  options,
  selectedOptions,
  onOptionChange,
  isDisabled,
  config,
}: DropdownSelectorProps) => {
  const selectedValue = selectedOptions ?? '';

  const handleChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const value = event.target.value;
      onOptionChange(value || '');
    },
    [onOptionChange]
  );

  const defaultOptionLabel = config?.defaultValueLabel ?? '--';

  return (
    <Select
      labelId="dropdown-selector-label"
      value={selectedValue}
      onChange={handleChange}
      disabled={isDisabled}
      displayEmpty
      renderValue={selected => {
        if (!selected) {
          return (
            <Typography variant="body1" color="textDisabled">
              {config?.emptyPlaceholder ?? defaultOptionLabel}
            </Typography>
          );
        }
        if (options) {
          const found = options.find(option => option.id === selected);
          return found ? found.name : defaultOptionLabel;
        }
        return defaultOptionLabel;
      }}
      MenuProps={{
        slotProps: {
          ...(config?.dropdownHeight ? { paper: { style: { maxHeight: config.dropdownHeight } } } : {}),
          list: {
            sx: {
              paddingTop: 0,
              paddingBottom: 0,
            },
          },
        },
      }}
      sx={{
        width: '100%',
        '& .MuiSelect-select': {
          padding: '2px 12px',
        },
      }}
    >
      <MenuItem value="">{defaultOptionLabel}</MenuItem>
      {options?.map(option => (
        <MenuItem key={option.id} value={option.id}>
          {option.name}
        </MenuItem>
      ))}
    </Select>
  );
};
